import type { JSONContent } from "@tiptap/core";
import type { EditorBlock, EditorPage } from "../editor.types";
import type { Json } from "../../../lib/database.types";
import { sectionBackground } from "../components/siteBackgrounds";

export const newBlock = (
  page: EditorPage,
  type: EditorBlock["block_type"],
  content: Json,
): EditorBlock => ({
  id: crypto.randomUUID(),
  source_block_id: crypto.randomUUID(),
  page_id: page.id,
  site_version_id: page.site_version_id,
  block_type: type,
  content,
  parent_source_block_id: null,
  target_page_source_id: null,
  asset_id: null,
  position: 0,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});
export const structuredNode = (
  block: EditorBlock,
  children: EditorBlock[] = [],
): JSONContent => ({
  type: "siteBlock",
  attrs: { block, children },
});
const supported = new Set([
  "doc",
  "paragraph",
  "heading",
  "text",
  "hardBreak",
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
  "horizontalRule",
]);
const supportedMarks = new Set([
  "bold",
  "italic",
  "strike",
  "code",
  "link",
  "underline",
]);
export function isSupported(node: JSONContent): boolean {
  return (
    supported.has(node.type ?? "") &&
    (node.marks ?? []).every((m) => supportedMarks.has(m.type)) &&
    (node.content ?? []).every(isSupported)
  );
}
/** A new row nested under `parent`, for columns that were saved incomplete. */
const childRecord = (
  parent: EditorBlock,
  type: EditorBlock["block_type"],
): EditorBlock => ({
  ...parent,
  id: crypto.randomUUID(),
  source_block_id: crypto.randomUUID(),
  parent_source_block_id: parent.source_block_id,
  block_type: type,
  content: {},
  asset_id: null,
  target_page_source_id: null,
});
/**
 * A side's vertical alignment: its own, else the one both sides shared
 * before each had its own, else the top.
 */
export const columnValign = (side: EditorBlock, columns: EditorBlock) =>
  (side.content as { valign?: string } | null)?.valign ??
  (columns.content as { valign?: string } | null)?.valign ??
  "top";
/** A section's settings, from its saved content or its editor node. */
const sectionSettings = (source: unknown) => {
  const { width, spacing, align } = (source ?? {}) as Record<string, string | undefined>;
  return {
    background: sectionBackground(source),
    width: width ?? "full",
    spacing: spacing ?? "regular",
    align: align ?? "left",
  };
};
const emptyLine = (): JSONContent => ({
  type: "paragraph",
  attrs: { record: null },
  content: [],
});
/** The editor nodes for the blocks directly under `parent` (null: the page). */
function toNodes(blocks: EditorBlock[], parent: string | null): JSONContent[] {
  return blocks
    .filter((b) => (b.parent_source_block_id ?? null) === parent)
    .sort((a, b) => a.position - b.position)
    .flatMap((block): JSONContent[] => {
      const rich = (block.content as { richText?: unknown }).richText;
      if (block.block_type === "text" && typeof rich === "string") {
        return [
          {
            type: "paragraph",
            attrs: { record: block },
            content: rich
              ? rich
                  .split("\n")
                  .flatMap((line, i) => [
                    ...(i ? [{ type: "hardBreak" }] : []),
                    ...(line ? [{ type: "text", text: line }] : []),
                  ])
              : [],
          },
        ];
      }
      if (
        block.block_type === "text" &&
        rich &&
        typeof rich === "object" &&
        isSupported(rich as JSONContent)
      ) {
        const doc = rich as JSONContent;
        return (doc.type === "doc" ? (doc.content ?? []) : [doc]).map(
          (node, i) => ({
            ...node,
            attrs: { ...node.attrs, record: i === 0 ? block : null },
          }),
        );
      }
      if (block.block_type === "columns") {
        const sides = blocks
          .filter(
            (b) =>
              b.parent_source_block_id === block.source_block_id &&
              b.block_type === "column",
          )
          .sort((a, b) => a.position - b.position)
          .slice(0, 2);
        while (sides.length < 2) sides.push(childRecord(block, "column"));
        return [
          {
            type: "columns",
            attrs: {
              record: block,
              style: (block.content as { style?: string }).style ?? "even",
            },
            content: sides.map((side) => {
              const content = toNodes(blocks, side.source_block_id);
              return {
                type: "column",
                attrs: { record: side, valign: columnValign(side, block) },
                content: content.length ? content : [emptyLine()],
              };
            }),
          },
        ];
      }
      if (block.block_type === "section") {
        const content = toNodes(blocks, block.source_block_id);
        return [
          {
            type: "section",
            attrs: {
              record: block,
              ...sectionSettings(block.content),
            },
            content: content.length ? content : [emptyLine()],
          },
        ];
      }
      // Unknown/legacy content remains intact as an opaque block.
      return [
        structuredNode(
          block,
          blocks.filter(
            (b) => b.parent_source_block_id === block.source_block_id,
          ),
        ),
      ];
    });
}
export function toDocument(blocks: EditorBlock[]): JSONContent {
  const content = toNodes(blocks, null);
  if (content.at(-1)?.type !== "paragraph" || content.at(-1)?.content?.length)
    content.push(emptyLine());
  return { type: "doc", content };
}
function cleanNode(node: JSONContent): JSONContent {
  const attrs = { ...node.attrs };
  delete attrs.record;
  const clean = { ...node };
  delete clean.attrs;
  delete clean.content;
  if (Object.keys(attrs).length) clean.attrs = attrs;
  if (node.content) clean.content = node.content.map(cleanNode);
  return clean;
}
export function fromDocument(
  doc: JSONContent,
  page: EditorPage,
): EditorBlock[] {
  const rows: EditorBlock[] = [];
  const used = new Set<string>();
  /** The node's own row, or a fresh one if it has none or is a copy. */
  const own = (record: EditorBlock | null | undefined, type: EditorBlock["block_type"]) => {
    const row =
      record && !used.has(record.source_block_id) ? record : newBlock(page, type, {});
    used.add(row.source_block_id);
    return row;
  };
  /**
   * A section's or column's blocks without the empty lines at their end
   * (left by clicking under them), which would show as gaps on the site.
   * One that holds nothing else keeps its line, as before.
   */
  const trimmed = (nodes: JSONContent[] = []) => {
    let end = nodes.length;
    while (end > 1 && nodes[end - 1].type === "paragraph" && !nodes[end - 1].content?.length) end--;
    return nodes.slice(0, end);
  };
  const walk = (nodes: JSONContent[], parent: string | null) =>
    nodes.forEach((node, position) => {
      if (node.type === "siteBlock") {
        const saved = node.attrs!.block as EditorBlock;
        // A pasted copy of a block gets its own identity.
        const block = used.has(saved.source_block_id)
          ? { ...saved, id: crypto.randomUUID(), source_block_id: crypto.randomUUID() }
          : saved;
        used.add(block.source_block_id);
        rows.push({ ...block, position, parent_source_block_id: parent });
        rows.push(
          ...((node.attrs!.children as EditorBlock[]) ?? []).map((child, i) => ({
            ...child,
            ...(block === saved
              ? {}
              : { id: crypto.randomUUID(), source_block_id: crypto.randomUUID() }),
            position: i,
            parent_source_block_id: block.source_block_id,
          })),
        );
      } else if (node.type === "section") {
        const block = own(node.attrs?.record, "section");
        rows.push({
          ...block,
          block_type: "section",
          position,
          parent_source_block_id: parent,
          content: sectionSettings(node.attrs),
        });
        walk(trimmed(node.content), block.source_block_id);
      } else if (node.type === "columns") {
        const block = own(node.attrs?.record, "columns");
        rows.push({
          ...block,
          block_type: "columns",
          position,
          parent_source_block_id: parent,
          content: { style: (node.attrs?.style as string | undefined) ?? "even" },
        });
        (node.content ?? []).forEach((side, i) => {
          const column = own(side.attrs?.record, "column");
          rows.push({
            ...column,
            block_type: "column",
            position: i,
            parent_source_block_id: block.source_block_id,
            content: { valign: (side.attrs?.valign as string | undefined) ?? "top" },
          });
          walk(trimmed(side.content), column.source_block_id);
        });
      } else {
        const record = own(node.attrs?.record, "text");
        rows.push({
          ...record,
          block_type: "text",
          position,
          parent_source_block_id: parent,
          content: {
            richText: { type: "doc", content: [cleanNode(node)] } as Json,
          },
        });
      }
    });
  walk(doc.content ?? [], null);
  return rows;
}
